import type { EngineSnapshot, Difficulty } from '../engine/engine';
import type { OperatorAction } from '../engine/core/actions';
import type { TagMeta } from '../engine/core/tags';
import type { TrendSeries } from '../engine/core/trends';

export const SPEEDS = [1, 5, 20, 100] as const;
export type Speed = (typeof SPEEDS)[number];

export interface InitOptions {
  plantId: string;
  scenarioId: string | null;
  /** Training lesson to run alongside the scenario. */
  lessonId?: string | null;
  seed: number;
  difficulty: Difficulty;
  /** Restore a save instead of starting fresh. */
  save?: string;
  /** Endless shift mode, in hours. Overrides `scenarioId`. */
  shiftModeHours?: number;
  /** Replay a saved action log at high speed instead of playing. */
  replay?: { entries: { step: number; action: OperatorAction }[]; toStep: number };
}

export type ToWorker =
  | ({ t: 'init' } & InitOptions)
  | { t: 'action'; action: OperatorAction }
  | { t: 'play' }
  | { t: 'pause' }
  | { t: 'setSpeed'; speed: number }
  | { t: 'stepOnce'; steps: number }
  | { t: 'getTrend'; id: number; tags: string[]; from: number; to: number; maxPoints: number }
  | { t: 'save'; id: number }
  | { t: 'recycle'; values: ArrayBuffer; quality: ArrayBuffer };

export interface WorkerStats {
  /** Simulated seconds actually achieved per real second. */
  achievedSpeed: number;
  /** True when the worker cannot keep up with the requested speed. */
  behind: boolean;
  stepMicros: number;
  trendBytes: number;
}

export interface ReplayProgress {
  step: number;
  total: number;
  done: boolean;
}

export type FromWorker =
  | { t: 'ready'; meta: TagMeta[]; trendTags: string[]; seed: number; scenarioId: string | null }
  | { t: 'snapshot'; snap: EngineSnapshot; running: boolean; speed: number; stats: WorkerStats }
  | { t: 'trend'; id: number; series: TrendSeries[] }
  | { t: 'save'; id: number; data: string }
  | { t: 'replay'; progress: ReplayProgress }
  | { t: 'error'; text: string };
